'use client'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import {
    applyCouponService,
    getCouponSuggestionsService,
    removeCouponService,
    updateCouponService,
} from '@/services/domain/cart/CouponService'
import { useCartStore } from '@/stores/useCartStore'
import { selectCouponName, useCouponStore } from '@/stores/useCouponStore'
import type { ICoupon, ICouponResponse } from '~/schemas/cart/coupon'
import { isApiError } from '~/schemas/errorSchemas'
import { useCartCache } from './useCartCache'

/**
 * Cupom de desconto no carrinho. Os services gravam o carrinho com o desconto na `useCartStore` e o cupom aplicado e o
 * escolhido na `useCouponStore`; os hooks atualizam o cache do carrinho.
 */

const EMPTY: ICoupon[] = []

/** A API busca o cupom pelo código normalizado: a comparação no front ignora caixa e espaços. */
const sameCode = (a: string | null | undefined, b: string | null | undefined) =>
    Boolean(a && b && a.trim().toUpperCase() === b.trim().toUpperCase())

// ===== APLICAR OU TROCAR =====

export function useApplyCoupon() {
    const { setCartCache, handleCartError } = useCartCache()

    const mutation = useMutation({
        mutationFn: ({ coupon, replace }: { coupon: string; replace: boolean }) =>
            replace ? updateCouponService(coupon) : applyCouponService(coupon),
        onSuccess: (response) => setCartCache(response.cart),
        onError: (error) => handleCartError(error),
    })

    /**
     * Aplica o cupom; com outro cupom já aplicado, troca (`update-coupon`). Com o mesmo cupom, resolve `null` sem
     * chamar a API. Rejeita com o ApiError (o motivo da recusa já saiu no toast).
     */
    const applyCoupon = useCallback(
        async (code: string): Promise<ICouponResponse | null> => {
            const applied = useCartStore.getState().data?.discountApplied?.discountCode
            if (sameCode(applied, code)) return null
            return mutation.mutateAsync({ coupon: code, replace: Boolean(applied) })
        },
        [mutation.mutateAsync],
    )

    return { applyCoupon, isApplying: mutation.isPending }
}

// ===== REMOVER =====

export function useRemoveCoupon() {
    const { setCartCache, handleCartError } = useCartCache()

    const mutation = useMutation({
        mutationFn: removeCouponService,
        onSuccess: (response) => setCartCache(response.cart),
        onError: (error) => handleCartError(error),
    })

    const removeCoupon = useCallback(() => mutation.mutateAsync(), [mutation.mutateAsync])

    return { removeCoupon, isRemoving: mutation.isPending }
}

// ===== CUPOM DA URL =====

/**
 * Aplica no carrinho o cupom escolhido (o da URL, gravado pelo `CouponFromUrl`) quando o carrinho tem itens e está
 * com outro cupom ou sem nenhum. Tenta uma vez por cupom enquanto a tela estiver montada; cupom recusado (400) deixa de
 * ser o escolhido, para não ser tentado de novo a cada visita.
 */
export function useApplyCouponFromUrl() {
    const chosen = useCouponStore(selectCouponName)
    const hasItems = useCartStore((state) => (state.data?.cartItems.length ?? 0) > 0)
    const applied = useCartStore((state) => state.data?.discountApplied?.discountCode)
    const { applyCoupon } = useApplyCoupon()
    const attempted = useRef<string | null>(null)

    useEffect(() => {
        if (!chosen || !hasItems || sameCode(applied, chosen) || attempted.current === chosen) return
        attempted.current = chosen
        applyCoupon(chosen).catch((error) => {
            if (isApiError(error) && error.statusCode === 400) useCouponStore.getState().clearCouponName()
        })
    }, [chosen, hasItems, applied, applyCoupon])
}

// ===== SUGESTÕES =====

export function useCouponSuggestions({ enabled = true }: { enabled?: boolean } = {}) {
    const query = useQuery({
        queryKey: ['coupon', 'suggestions'],
        queryFn: getCouponSuggestionsService,
        enabled,
    })

    return { suggestions: query.data ?? EMPTY, isLoading: query.isLoading, isError: query.isError }
}
